import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useParams} from '@github-ui/react-router'
import {Breadcrumbs, PageHeader, PageLayout, RelativeTime} from '@primer/react'
import {artifactRegistryShowRoute} from './artifact-registry-route'
import type {ArtifactRegistryShowRoutePayload} from '../types'
import {artifactIsDetectedBy} from '../types'
import {DeploymentRecordList} from '../components/DeploymentRecordList'
import {SourceRepo} from '../components/SourceRepo'
import {StorageRegistry} from '../components/StorageRegistry'
import styles from './ArtifactRegistryShow.module.css'
import {RegistryAttestationsList} from '../components/RegistryAttestationsList'
import {ArrowLeftIcon} from '@primer/octicons-react'

export function ArtifactRegistryShow() {
  const payload: ArtifactRegistryShowRoutePayload = useRouteQuery(artifactRegistryShowRoute, 'mainQuery').data
  const routeParams = useParams<{org: string}>()
  const org = routeParams.org as string
  const {artifact, attestations, deployments, storageRecord, deploymentPageInfo} = payload
  const provenanceAttestation = attestations.find(attestation => attestation.id === artifact.attestation_id)

  const hasLastUpdated = artifact.updated_at && artifact.updated_at !== artifact.created_at
  const detectedBy = artifactIsDetectedBy(artifact)
  return (
    <PageLayout>
      <PageLayout.Header>
        <Breadcrumbs>
          <Breadcrumbs.Item href={`/orgs/${org}/artifacts`}>
            <span className="d-inline-flex flex-items-center gap-1">
              <ArrowLeftIcon aria-hidden="true" />
              <span>Linked artifacts</span>
            </span>
          </Breadcrumbs.Item>
        </Breadcrumbs>
        <PageHeader>
          <PageHeader.TitleArea className="tmp-mt-4">
            <PageHeader.Title>
              <span className="f1 text-light">
                {artifact.artifact_name}
                {artifact.version && <span className="fgColor-muted"> {artifact.version}</span>}
              </span>
            </PageHeader.Title>
          </PageHeader.TitleArea>
          <PageHeader.Description>
            <span className="fgColor-muted d-flex flex-items-center gap-2">
              <span>
                Detected <RelativeTime datetime={artifact.created_at} />
                {detectedBy && <> by {`${detectedBy}`}</>}
              </span>
              {hasLastUpdated && (
                <>
                  <span>·</span>
                  <span>
                    Updated <RelativeTime datetime={artifact.updated_at} />
                  </span>
                </>
              )}
            </span>
          </PageHeader.Description>
        </PageHeader>
      </PageLayout.Header>
      <PageLayout.Content as="div">
        <div className={styles.MainContent}>
          <div>
            <StorageRegistry data={storageRecord} />

            <DeploymentRecordList deployments={deployments} deploymentPageInfo={deploymentPageInfo} />

            {attestations.length > 0 && <RegistryAttestationsList attestations={attestations} org={org} />}
          </div>

          <div className={styles.Sidebar}>
            <SourceRepo
              attestation={provenanceAttestation}
              repositoryName={artifact.repository_name}
              org={org}
              repository_is_private={artifact.repository_is_private}
              digest={artifact.digest}
            />
          </div>
        </div>
      </PageLayout.Content>
    </PageLayout>
  )
}
